import { useEffect, useState } from "react";
import { ArrowUpRight, Mail, MapPin, MessageCircle, RefreshCw, Send, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Eyebrow } from "./Reveal";
import { useSite, waLink } from "@/context/SiteContext";
import { api, formatApiErrorDetail } from "@/lib/api";

const SUBJECTS = ["Reservation", "Experiences", "Events & Weddings", "Press", "General"];

export const Contact = () => {
  const { settings } = useSite();
  const [form, setForm] = useState({ name: "", email: "", subject: "Reservation", body: "", company: "" });
  const [captcha, setCaptcha] = useState(null);
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [sending, setSending] = useState(false);

  const loadCaptcha = () => {
    setCaptchaAnswer("");
    api.get("/captcha").then((r) => setCaptcha(r.data)).catch(() => setCaptcha(null));
  };

  useEffect(loadCaptcha, []);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/messages", {
        ...form,
        captcha_token: captcha?.token || "",
        captcha_answer: captchaAnswer,
      });
      toast.success("Message sent — our concierge will reply shortly.");
      setForm({ name: "", email: "", subject: "Reservation", body: "", company: "" });
      loadCaptcha();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      loadCaptcha();
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-20 sm:py-28">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Reveal>
            <Eyebrow>Contact Us</Eyebrow>
            <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight text-balance">
              Tell us when the island should expect you
            </h2>
            <p className="mt-5 text-base text-seastone leading-relaxed max-w-md">
              Our concierge replies personally to every message — usually within a few hours.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-8 space-y-4 text-sm">
              <p className="flex items-start gap-3" data-testid="contact-address">
                <MapPin size={16} className="text-azure mt-0.5 shrink-0" />
                {settings?.address || "Jungutbatu, Nusa Lembongan, Bali, Indonesia"}
              </p>
              <a href={waLink(settings)} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-link" className="flex items-center gap-3 hover:text-azure transition-colors">
                <MessageCircle size={16} className="text-azure shrink-0" />
                +62 813-3919-2688 (WhatsApp)
              </a>
              {settings?.email && (
                <a href={`mailto:${settings.email}`} data-testid="contact-email-link" className="flex items-center gap-3 hover:text-azure transition-colors">
                  <Mail size={16} className="text-azure shrink-0" />
                  {settings.email}
                </a>
              )}
              {settings?.maps_url && (
                <a href={settings.maps_url} target="_blank" rel="noopener noreferrer" data-testid="contact-maps-link" className="inline-flex items-center gap-1.5 text-azure hover:text-azure-dark">
                  Get directions <ArrowUpRight size={13} />
                </a>
              )}
            </div>
          </Reveal>
        </div>

        <Reveal className="lg:col-span-7" delay={0.15}>
          <form onSubmit={submit} className="bg-white rounded-3xl border border-ink/10 p-6 sm:p-8 shadow-[0_16px_50px_rgba(14,27,46,0.08)]" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Your name"
                data-testid="contact-name-input"
                className="rounded-xl border border-ink/15 bg-sand px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-azure/40"
              />
              <input
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Email address"
                data-testid="contact-email-input"
                className="rounded-xl border border-ink/15 bg-sand px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-azure/40"
              />
            </div>
            {/* honeypot — hidden from humans, bots fill it */}
            <input
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={form.company}
              onChange={(e) => setForm({ ...form, company: e.target.value })}
              className="hidden"
              aria-hidden="true"
              data-testid="contact-honeypot-input"
            />
            <div className="mt-4 flex flex-wrap gap-2" data-testid="contact-subject-group">
              {SUBJECTS.map((s) => (
                <button
                  type="button"
                  key={s}
                  onClick={() => setForm({ ...form, subject: s })}
                  data-testid={`contact-subject-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={`rounded-full px-4 py-2 text-xs transition-colors ${form.subject === s ? "bg-ink text-sand" : "bg-sand2 text-ink/70 hover:bg-ink/10"}`}
                >
                  {s}
                </button>
              ))}
            </div>
            <textarea
              required
              rows={5}
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              placeholder="Tell us your dates, how many are travelling, and anything we should prepare…"
              data-testid="contact-message-input"
              className="mt-4 w-full rounded-xl border border-ink/15 bg-sand px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-azure/40"
            />
            <div className="mt-4 flex flex-wrap items-end gap-3" data-testid="contact-captcha-group">
              <div>
                <label className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.18em] text-seastone mb-1.5">
                  <ShieldCheck size={12} /> I'm not a robot — what is {captcha?.question ?? "…"}?
                </label>
                <input
                  required
                  inputMode="numeric"
                  value={captchaAnswer}
                  onChange={(e) => setCaptchaAnswer(e.target.value)}
                  placeholder="Your answer"
                  data-testid="contact-captcha-input"
                  className="w-36 rounded-xl border border-ink/15 bg-sand px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-azure/40"
                />
              </div>
              <button
                type="button"
                onClick={loadCaptcha}
                data-testid="contact-captcha-refresh"
                className="h-[46px] w-[46px] grid place-items-center rounded-xl border border-ink/15 text-seastone hover:text-ink transition-colors"
                aria-label="New captcha question"
              >
                <RefreshCw size={15} />
              </button>
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-send-button"
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-azure text-sand text-sm font-medium px-7 py-3 transition-all hover:bg-azure-dark hover:shadow-[0_0_0_5px_rgba(26,98,137,0.16)] disabled:opacity-60"
            >
              {sending ? "Sending…" : "Send Message"} <Send size={14} />
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
